import { useQuery } from "@tanstack/react-query";
import api from "../../http";

export interface UserAddress {
	city: string;
	state: string;
	street: string;
	district: string;
	number: string;
	zipCode: string;
	complement: string | null;
}

export interface UserSubscriptionPlan {
	_id: string;
	itemId: string | null;
	name: string;
	advantages: string[];
	price: number;
	paymentMethods: string[];
	interval: string;
	intervalCount: number;
	gracePeriod: string | null;
	notifyUnpayment: boolean;
	notifyInvoice: boolean;
	status: string;
	isListable: boolean;
	recurrentPayment: boolean;
	maxInstallments: number;
	maxProps: number;
	hierarchy: string;
	maxSamples: number;
	__v: number;
	createdAt: string;
	updatedAt: string;
}

export interface UserSubscriptionPaymentFailure {
	code: string | null;
	message: string | null;
	failedAt: string | null;
	rawStatus: string | null;
}

export interface UserSubscriptionRefund {
	status: 'requested' | 'processing' | 'succeeded' | 'failed';
	requestedAt: string | null;
	resolvedAt: string | null;
	reason: string | null;
	gatewayRefundId: string | null;
	gatewayError: string | null;
	legalBasis: string | null;
}

export interface UserSubscription {
	plan: UserSubscriptionPlan;
	status: string;
	paymentStatus: string;
	paymentMethod: string | null;
	expiresIn: string;
	manualCreation: boolean;
	waitCancel: boolean;
	signedAt: string;
	updatedAt: string | null;
	totalProps: number;
	totalSamples: number;
	paymentFailure: UserSubscriptionPaymentFailure | null;
	refund: UserSubscriptionRefund | null;
}

export interface UserGatewayMetadata {
	providerName: string | null;
	customerId: string | null;
	subscriptionId: string | null;
	checkoutId: string | null;
	billingReferenceId: string | null;
	checkoutUrl: string | null;
	cardId: string | null;
	boletoURL: string | null;
	orderId: string | null;
	chargeId: string | null;
	receiptUrl: string | null;
}

export interface UserLastKnownAddress {
	zipCode: string;
	city: string;
	state: string;
	region: string;
}

export interface UserFormData {
	name: string;
	surName: string;
	email: string;
	document: string;
	documentType: string;
	contactEmail: string;
	phoneNumber: string;
	profileImage: string | null;
	status: boolean;
	isMaster: boolean;
	role: string;
	address: UserAddress;
}

export function useGetUserById(id: string, options?: { enabled?: boolean }) {
	return useQuery({
		enabled: options?.enabled,
		queryKey: ['userId', id],
		queryFn: async () => (await api.get(`/management/user/findOne/${id}`)).data,
		select: (data) => {
			if (!data) return null;
			const {
				_id,
				name,
				surName,
				email,
				binding,
				document,
				documentType,
				contactEmail,
				address,
				phoneNumber,
				profileImage,
				avatar,
				status,
				isMaster,
				role,
				subscription,
				gatewayMetadata,
				propCounter,
				lastKnownAddress,
				totalAnalysis,
				totalAdvices,
				createdAt,
				updatedAt,
				lastLogin,
			} = data;

			return {
				id: _id as string,
				binding: binding as string | null,
				propCounter: propCounter as number,
				totalAnalysis: totalAnalysis as number,
				totalAdvices: totalAdvices as number,
				lastKnownAddress: lastKnownAddress as UserLastKnownAddress,
				subscription: subscription as UserSubscription,
				gatewayMetadata: gatewayMetadata as UserGatewayMetadata,
				createdAt: createdAt as string,
				updatedAt: updatedAt as string,
				lastLogin: lastLogin as string,
				avatarUrl: avatar?.url as string | undefined,
				formData: {
					name,
					surName,
					email,
					document,
					documentType,
					contactEmail,
					address,
					phoneNumber: phoneNumber ?? '',
					profileImage,
					status,
					isMaster,
					role,
				} as UserFormData,
			};
		},
	});
}